import React, { useCallback, useEffect, useState } from 'react';
import { useController, ApplicationView as view } from '@aha-app/mvc';
import Project from 'components/framework/models/Project';
import Spinner from 'components/common/Spinner';
import { ProjectTree } from 'components/framework/models/ProjectTree';
import ProjectSelect from 'components/common/ProjectSelect';
import { showNoticeMessage } from '../../javascripts/messages';
import Modal from '@aha-app/react-bootstrap-modal';

/**
 * @type {React.FC<{}>}
 */
const PromoteToWorkspaceModal = () => {
  const controller = useController();
  const records = controller.state.records;
  const showModal = controller.state.showModal;
  const [selectedProject, setSelectedProject] = useState();
  const [projectTree, setProjectTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [showNoReleaseError, setShowNoReleaseError] = useState(false);

  const closeModal = useCallback(() => {
    controller.actionHideModal();
  }, [controller]);

  const handleSubmit = useCallback(() => {
    setSaveDisabled(true);
    const releaseId = selectedProject.defaultRelease?.id;
    if (releaseId) {
      Promise.all(
        records.map(record =>
          record.update(
            { release: { id: releaseId } },
            { args: { __reactivelyUpdate: true } }
          )
        )
      ).then(() => {
        showNoticeMessage('Records promoted to workspace');
      });
    }
    closeModal();
  }, [closeModal, records, selectedProject]);

  const setProject = useCallback(project => {
    if (!project.defaultRelease) {
      setSaveDisabled(true);
      setShowNoReleaseError(true);
    } else {
      setSaveDisabled(false);
      setShowNoReleaseError(false);
    }
    setSelectedProject(project);
  }, []);

  useEffect(() => {
    ProjectTree.load(
      Project.select('id', 'name', 'referencePrefix', 'isProductLine')
        .merge({ defaultRelease: ['id'] })
        .where({ teams: false })
    ).then(projectTree => {
      setProjectTree(projectTree);
      setLoading(false);
    });
  }, [records]);

  useEffect(() => {
    document.addEventListener('aha-modal-header:close', closeModal);

    return () => {
      document.removeEventListener('aha-modal-header:close', closeModal);
    };
  }, [closeModal]);

  return (
    <Modal show={showModal} className='modal-big' onHide={closeModal}>
      <aha-modal-header modal-title='Promote to workspace' />
      <div className='modal-body modal-body--web-components'>
        {loading ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '70px',
            }}
          >
            <Spinner small />
          </div>
        ) : (
          <div>
            <aha-field>
              <div slot='label'>Workspace</div>
              <ProjectSelect
                projectTree={projectTree}
                onSelected={setProject}
                filter={project => !project.isProductLine}
                selectroClasses='input-xlarge'
                nil='Choose a workspace'
              />
              {showNoReleaseError && (
                <div slot='error'>
                  This workspace cannot be selected as it does not have a
                  default release
                </div>
              )}
            </aha-field>
          </div>
        )}
      </div>
      <aha-modal-footer>
        <div slot='secondary_actions'>
          <button className='btn btn-secondary' onClick={closeModal}>
            Cancel
          </button>
        </div>
        <button
          className='btn btn-primary'
          onClick={handleSubmit}
          disabled={loading || !selectedProject || saveDisabled}
        >
          Save
        </button>
      </aha-modal-footer>
    </Modal>
  );
};

export default view(PromoteToWorkspaceModal);
